import axios from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { useTenant } from "src/components/LoggedInAdminProvider";
import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import { CAMPAIGN_START_CSV_IMPORT } from "src/graphql/campaignContactImport";
import CREATE_UPLOAD from "src/graphql/createUpload";
import {
  campaignUploadContacts,
  campaignUploadContactsVariables
} from "src/graphql/generated/campaignUploadContacts";
import {
  createUpload,
  createUploadVariables
} from "src/graphql/generated/createUpload";
import {
  getCampaign_campaign,
  getCampaign_campaign_contactImportJob
} from "src/graphql/generated/getCampaign";
import {
  CampaignIntegrationInput,
  JobStatus,
  JobType,
  UploadType,
  UploadVisibility
} from "src/graphql/generated/globalTypes";
import { CSVMetadata, CSVMetadataValid, parseCsv } from "src/util/csv";
import useMutation from "src/util/useMutation";

import FilePicker from "../../../../components/form/FilePicker";
import { useCampaignEditEvents } from "../../CampaignEditorsProvider";
import { ImportStatus } from "../ContactSources";
import JobComplete from "../JobComplete";
import JobRunning from "../JobRunning";
import ParsedFile from "./ParsedFile";
import ParsingFile from "./ParsingFile";
import Uploading from "./Uploading";

type State =
  | "PICKING_CSV"
  | "PARSING_CSV"
  | "PARSED_CSV"
  | "UPLOADING_CSV"
  | "IMPORTING_CSV"
  | "CSV_IMPORTED";

const stateFromJob = (job: getCampaign_campaign_contactImportJob) => {
  switch (job.status) {
    case JobStatus.QUEUED:
    case JobStatus.RUNNING:
      return "IMPORTING_CSV";
    default:
      return "CSV_IMPORTED";
  }
};

export const hasValidCsvImport = (campaign: getCampaign_campaign): boolean => {
  if (campaign.contactImportJob?.type !== JobType.CAMPAIGN_CONTACTS_UPLOAD) {
    return false;
  }

  const args = JSON.parse(campaign.contactImportJob!.args ?? "{}");
  const csvVanIdIntegration: CampaignIntegrationInput | null =
    args.columnMappings?.vanIdColumnAndSettings?.integration;
  if (campaign.vanIntegration) {
    return (
      csvVanIdIntegration?.vanIntegrationId ===
        campaign.vanIntegration.vanIntegration.id &&
      csvVanIdIntegration?.databaseMode === campaign.vanIntegration.databaseMode
    );
  } else {
    return !csvVanIdIntegration;
  }
};

const CsvImport: React.FC<{
  campaign: getCampaign_campaign;
  onChangeImportStatus: (status: ImportStatus) => void;
  onClearContacts: () => void;
  overwriting: boolean;
}> = ({ campaign, onChangeImportStatus, onClearContacts, overwriting }) => {
  const tenant = useTenant();

  const [state, setState] = useState<State>(
    hasValidCsvImport(campaign) && !overwriting
      ? stateFromJob(campaign.contactImportJob!)
      : "PICKING_CSV"
  );
  useEffect(() => {
    if (hasValidCsvImport(campaign)) {
      setState(stateFromJob(campaign.contactImportJob!));
    }
  }, [campaign]);
  useEffect(() => {
    if (overwriting) setState("PICKING_CSV");
  }, [overwriting]);
  useEffect(() => {
    switch (state) {
      case "PICKING_CSV":
      case "PARSED_CSV":
        onChangeImportStatus("PREPARING");
        break;
      case "PARSING_CSV":
      case "UPLOADING_CSV":
      case "IMPORTING_CSV":
        onChangeImportStatus("IN_PROGRESS");
        break;
      case "CSV_IMPORTED":
        onChangeImportStatus("DONE");
    }
  }, [state, onChangeImportStatus]);

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvParseProgress, setCsvParseProgress] = useState(0);
  const [csvMetadata, setCsvMetadata] = useState<CSVMetadata | null>(null);
  const [csvUploadProgress, setCsvUploadProgress] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const sendEditEvent = useCampaignEditEvents();

  const [getPrivateUploadURL] = useMutation<
    createUpload,
    createUploadVariables
  >(CREATE_UPLOAD, {
    variables: {
      campaignId: campaign.id,
      type: UploadType.CAMPAIGN_CONTACTS,
      contentType: "application/csv",
      fileName: csvFile?.name || "contacts.csv",
      visibility: UploadVisibility.PRIVATE
    },
    onErrorSnackbarMessage: "Failed to access upload storage"
  });

  const [startCsvUploadJob] = useMutation<
    campaignUploadContacts,
    campaignUploadContactsVariables
  >(CAMPAIGN_START_CSV_IMPORT, {
    onErrorSnackbarMessage: "Failed to start contact import"
  });

  async function startCsvUpload({
    phoneNumberColumn,
    vanIdColumn
  }: {
    phoneNumberColumn: string;
    vanIdColumn?: string;
  }) {
    setState("UPLOADING_CSV");
    setCsvUploadProgress(0);

    // Get an upload URL
    const uploadURL = await getPrivateUploadURL();
    if (uploadURL.errors) {
      setState("PARSED_CSV");
      return;
    }

    // Upload the file
    try {
      await axios.put(uploadURL.data!.createUpload.presignedPutURL, csvFile, {
        onUploadProgress(evt) {
          setCsvUploadProgress(evt.loaded / evt.total);
        },
        headers: {
          "Content-Type": "application/csv"
        }
      });
    } catch (e) {
      console.error(e);
      setState("PARSED_CSV");
      enqueueSnackbar("Failed to upload CSV", { variant: "error" });
      return;
    }

    // Trigger the job
    const startJobResult = await startCsvUploadJob({
      variables: {
        campaignId: campaign.id,
        uploadToken: uploadURL.data!.createUpload.token,
        columnMappings: {
          phoneNumberColumn: phoneNumberColumn,
          vanIdColumnAndSettings:
            vanIdColumn && campaign.vanIntegration
              ? {
                  column: vanIdColumn,
                  integration: {
                    vanIntegrationId: campaign.vanIntegration.vanIntegration.id,
                    databaseMode: campaign.vanIntegration.databaseMode
                  }
                }
              : null
        },
        estimatedRowCount: (csvMetadata as CSVMetadataValid).rows
      }
    });
    if (startJobResult.errors) {
      setState("PARSED_CSV");
      return;
    } else {
      sendEditEvent();
    }
  }

  async function handleSelectCsv(file: File) {
    setCsvFile(file);
    setState("PARSING_CSV");

    const metadata = await parseCsv(file, setCsvParseProgress);

    setCsvMetadata(metadata);
    setState("PARSED_CSV");
  }

  switch (state) {
    case "PICKING_CSV":
      return (
        <FilePicker
          dropzoneText="Drop a CSV here or click to select a file"
          onPickFile={handleSelectCsv}
          maxSizeMB={tenant?.csvUploadLimitMb}
          requiredMimeType={"text/csv"}
          requiredMimeTypeDescription={"CSV"}
        />
      );
    case "PARSING_CSV":
      return (
        <>
          <RouteLeavingGuard />
          <ParsingFile nRows={csvParseProgress} />
        </>
      );
    case "PARSED_CSV":
      return (
        <>
          <RouteLeavingGuard />
          <ParsedFile
            campaign={campaign}
            onClearFile={() => setState("PICKING_CSV")}
            fileName={csvFile!.name}
            metadata={csvMetadata!}
            onStartImport={startCsvUpload}
          />
        </>
      );
    case "UPLOADING_CSV":
      return (
        <>
          <RouteLeavingGuard />
          <Uploading progress={csvUploadProgress} />
        </>
      );
    case "IMPORTING_CSV":
      return <JobRunning job={campaign.contactImportJob!} />;
    case "CSV_IMPORTED":
      return (
        <JobComplete
          onClearFile={() => {
            setState("PICKING_CSV");
            onClearContacts();
          }}
          campaign={campaign}
          dataSourceFieldLabel="CSV column"
        />
      );
  }
};

export default CsvImport;
